/**
 * @deprecated — All map state has been migrated to the Zustand store at
 * `src/stores/map/mapStore.ts`. This file is retained only for type re-exports
 * and backward-compatibility with test files that still use Redux dispatch.
 * Removal deadline: 2026-04-30
 */
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { Geometry, Position } from "@turf/helpers";
import { CLEAR_MAP_ID } from "constants/app.constants";
import _debounce from "lodash/debounce";
import { AppThunk } from "store/store";
import type {
  ConfirmationChoice,
  Envelope,
  FlyToSpecificPoint,
  INetworkDepthLegend,
  ISelectedWell,
  MapDisplaySettings,
  WellNetworkRequest
} from "stores/map/mapTypes";

import { IMapResult } from "api/map";

import { mBinSize } from "models/binSize";
import { DEFAULT_LATITUDE, DEFAULT_LONGITUDE, DEFAULT_ZOOM } from "models/mapDefaults";

import { EntityKind } from "../../../models/entityKind";

export type {
  ConfirmationChoice,
  Envelope,
  FlyToSpecificPoint,
  INetworkDepthLegend,
  ISelectedWell,
  MapDisplaySettings,
  WellNetworkRequest
} from "stores/map/mapTypes";
export type { IMapRequest } from "types/maps/request";

export interface RasterLayerOpacity {
  layer: string;
  id: string;
  opacity: number;
}

export interface MapState {
  attentionWells: string[];
  displaySettings: MapDisplaySettings;
  exploitableArea: Geometry[];
  flyToGroup: string;
  groupBy;
  hoveredWell;
  isDebuggingMap: boolean;
  layers;
  mapExtent;
  mapState;
  selectedWells;
  selectedFacilities;
  txnId;
  typeLogWells;
  viewLock: boolean;
  xdaInterceptData;
  xdaLineRequest;
  overrideSizeLimit: boolean;
  hasToConfirmSize: boolean;
  facilityOverrideSizeLimit: boolean;
  facilityHasToConfirmSize: boolean;
  /**
   * Loading state for well map data
   */
  isLoadingWellMap: boolean;
  /**
   * Loading state for facility map data
   */
  isLoadingFacilityMap: boolean;
  /**
   * User's choice when prompted with wells series size confirmation dialog
   */
  wellsConfirmationChoice: Maybe<ConfirmationChoice>;
  /**
   * User's choice when prompted with facility series size confirmation dialog
   */
  facilityConfirmationChoice: Maybe<ConfirmationChoice>;
  /**
   * when set will draw attention to the polygon on the map
   * attention layers are on top of everything
   */
  attentionPolygon: GeoJSON.Polygon;
  flyToSpecificPoint?: FlyToSpecificPoint;
  networkDepthLegend: INetworkDepthLegend;
  /**
   * Controls visibility of network options panel
   */
  showNetworkOptions: boolean;
  /**
   * Facility ID to trigger highlighting of contributing wells from midstream navigator
   */
  highlightContributingWellsForFacility: Maybe<string>;
  /**
   * Well network map request state for network visualization
   */
  wellNetworkMapRequest: WellNetworkRequest;
  /**
   * Flag to track when WellNetwork filter has been activated for pan-to-bounds
   */
  isWellNetworkFilterActivated: boolean;
  networkEnvelope: Envelope;
  salesEnvelope: Envelope;
  /**
   * IPDB layer loaded status tracking
   */
  ipdbLayersLoaded: {
    geoModel: boolean;
    controlWells: boolean;
  };
  /**
   * Optional array of resource plays to filter the base well layer
   */
  baseLayerResourcePlays?: string[];
}

const initialState: MapState = {
  txnId: {
    id: "",
    facilityId: "",
    legend: {
      legendItems: [],
      title: ""
    },
    facilityLegend: {
      legendItems: [],
      title: ""
    },
    filterId: "",
    bBox: {
      sw: [],
      ne: []
    }
  },

  attentionPolygon: undefined,
  exploitableArea: [],
  mapExtent: {
    type: "Polygon",
    coordinates: [] as Position[][]
  } as GeoJSON.Polygon,
  groupBy: {},
  layers: [],
  selectedWells: {},
  selectedFacilities: {},
  typeLogWells: [],
  flyToGroup: undefined,
  attentionWells: [],
  // @deprecated — migrated to hoveredWellAtom (atoms/interaction.ts). No new writes. Removal deadline: next release cycle after Phase 1 sign-off.
  hoveredWell: [],
  xdaInterceptData: {
    line: [],
    points: []
  },
  xdaLineRequest: {
    line: [],
    wells: []
  },
  mapState: {
    lat: DEFAULT_LATITUDE,
    lng: DEFAULT_LONGITUDE,
    zoom: DEFAULT_ZOOM
  },
  viewLock: false,
  isDebuggingMap: false,
  displaySettings: {
    wellLineThickness: 2,
    wellDotThickness: 2,
    wellLabelSize: 9,
    facilityLabelSize: 9,
    facilityIconSize: 1,
    shapefileDetailsLabelSize: 9,
    shapefileNameLabelSize: 9,
    sectionLabelSize: 9,
    layerZoomSettings: {},
    labelDensity: {}
  },
  overrideSizeLimit: false,
  hasToConfirmSize: false,
  facilityOverrideSizeLimit: false,
  facilityHasToConfirmSize: false,
  isLoadingWellMap: false,
  isLoadingFacilityMap: false,
  wellsConfirmationChoice: null,
  facilityConfirmationChoice: null,
  flyToSpecificPoint: undefined,
  networkDepthLegend: {
    legendItems: [],
    visible: false,
    title: "Network Depth"
  },
  showNetworkOptions: false,
  highlightContributingWellsForFacility: null,
  wellNetworkMapRequest: {
    wellIds: [],
    date: null,
    onlyPrimaryPath: true,
    hideConnectionsLessThanPercent: 0
  },
  isWellNetworkFilterActivated: false,
  networkEnvelope: null,
  salesEnvelope: null,
  ipdbLayersLoaded: {
    geoModel: false,
    controlWells: false
  },
  baseLayerResourcePlays: undefined
};

const mapSlice = createSlice({
  name: "map",
  initialState,
  reducers: {
    setTxnId(state, action: PayloadAction<IMapResult>) {
      const incoming = action.payload;
      // Skip update if tile-relevant IDs haven't changed.
      // Prevents redundant MapboxWrapper re-renders from clearFacilityMap/clearWellMap
      // dispatching the same values on every pan.
      if (
        state.txnId?.id === incoming?.id &&
        state.txnId?.facilityId === incoming?.facilityId
      ) {
        return;
      }
      state.txnId = incoming;
    },
    setMapExtent(state, action: PayloadAction<GeoJSON.Polygon>) {
      state.mapExtent = action.payload;
    },
    setNetworkDepthLegend(state, action: PayloadAction<INetworkDepthLegend>) {
      state.networkDepthLegend = action.payload;
    },
    clearNetworkDepthLegend(state) {
      state.networkDepthLegend = {
        legendItems: [],
        visible: false,
        title: "Network Depth"
      };
    },
    setBaseLayerResourcePlays(state, action: PayloadAction<Maybe<string[]>>) {
      state.baseLayerResourcePlays = action.payload;
    },
    setShowNetworkOptions(state, action: PayloadAction<boolean>) {
      state.showNetworkOptions = action.payload;
    },
    setHighlightContributingWellsForFacility(state, action: PayloadAction<string>) {
      state.highlightContributingWellsForFacility = action.payload;
    },
    clearHighlightContributingWellsForFacility(state) {
      state.highlightContributingWellsForFacility = null;
    },
    setWellNetworkMapRequest(state, action: PayloadAction<WellNetworkRequest>) {
      state.wellNetworkMapRequest = action.payload;
    },
    clearWellNetworkMapRequest(state) {
      state.wellNetworkMapRequest = {
        wellIds: [],
        date: null,
        onlyPrimaryPath: true,
        hideConnectionsLessThanPercent: 0
      };
    },
    setWellNetworkFilterActivated(state, action: PayloadAction<boolean>) {
      state.isWellNetworkFilterActivated = action.payload;
    },
    setXdaInterceptData(state, action) {
      state.xdaInterceptData = action.payload;
    },
    setXdaLineRequest(state, action) {
      state.xdaLineRequest = action.payload;
    },
    setFlyToGroup(state, action: PayloadAction<string>) {
      state.flyToGroup = action.payload;
    },
    setMapGroupBy(state, action) {
      state.groupBy = action.payload;
    },
    setSelectedWells(state, action: PayloadAction<{ [name: string]: ISelectedWell }>) {
      state.selectedWells = action.payload;
    },
    setSelectedFacilities(
      state,
      action: PayloadAction<{ [name: string]: ISelectedWell }>
    ) {
      state.selectedFacilities = action.payload;
    },
    setTypeLogWells(state, action: PayloadAction<ISelectedWell[]>) {
      state.typeLogWells = action.payload;
    },
    setAttentionWells(state, action: PayloadAction<string[]>) {
      if (action.payload.length !== state.attentionWells.length) {
        state.attentionWells = action.payload;
        return;
      }
      const currentSet = new Set(state.attentionWells);
      const nextSet = new Set(action.payload);
      if (currentSet.size !== nextSet.size) {
        state.attentionWells = action.payload;
        return;
      }
      for (const item of nextSet) {
        if (!currentSet.has(item)) {
          state.attentionWells = action.payload;
          return;
        }
      }
    },
    setLayers(state, action) {
      state.layers = action.payload;
    },
    /**
     * @deprecated Well hover state has been migrated to `hoveredWellAtom` in
     * `atoms/interaction.ts`. Do not dispatch this action from new code.
     * Removal deadline: next release cycle after Phase 1 sign-off.
     */
    setHoveredWell(state, action) {
      state.hoveredWell = action.payload;
    },
    setAttentionPolygon(state, action: PayloadAction<GeoJSON.Polygon>) {
      state.attentionPolygon = action.payload;
    },
    setMapState(state, action) {
      state.mapState = action.payload;
    },
    setViewLock(state, action: PayloadAction<boolean>) {
      state.viewLock = action.payload;
    },
    setExploitableArea(state, action: PayloadAction<Geometry[]>) {
      state.exploitableArea = action.payload;
    },
    setIsDebuggingMap(state, action: PayloadAction<boolean>) {
      state.isDebuggingMap = action.payload;
    },
    setDisplaySettings(state, action: PayloadAction<MapDisplaySettings>) {
      state.displaySettings = action.payload;
    },
    setLabelDensity(state, action: PayloadAction<{ layer: string; value: number }>) {
      const { layer, value } = action.payload;
      state.displaySettings.labelDensity[layer] = Math.max(0, Math.min(1, value));
    },
    setOverrideSizeLimit(state, action: PayloadAction<boolean>) {
      state.overrideSizeLimit = action.payload;
    },
    setHasToConfirmSize(state, action: PayloadAction<boolean>) {
      state.hasToConfirmSize = action.payload;
    },
    setFacilityOverrideSizeLimit(state, action: PayloadAction<boolean>) {
      state.facilityOverrideSizeLimit = action.payload;
    },
    setFacilityHasToConfirmSize(state, action: PayloadAction<boolean>) {
      state.facilityHasToConfirmSize = action.payload;
    },
    setIsLoadingWellMap(state, action: PayloadAction<boolean>) {
      state.isLoadingWellMap = action.payload;
    },
    setIsLoadingFacilityMap(state, action: PayloadAction<boolean>) {
      state.isLoadingFacilityMap = action.payload;
    },
    setWellsConfirmationChoice(state, action: PayloadAction<Maybe<ConfirmationChoice>>) {
      state.wellsConfirmationChoice = action.payload;
    },
    setFacilityConfirmationChoice(
      state,
      action: PayloadAction<Maybe<ConfirmationChoice>>
    ) {
      state.facilityConfirmationChoice = action.payload;
    },
    setFlyToSpecificPoint(state, action: PayloadAction<FlyToSpecificPoint>) {
      state.flyToSpecificPoint = action.payload;
    },
    setNetworkEnvelope(state, action: PayloadAction<Envelope>) {
      state.networkEnvelope = action.payload;
    },
    setSalesEnvelope(state, action: PayloadAction<Envelope>) {
      state.salesEnvelope = action.payload;
    },
    setIpdbGeoModelLoaded(state, action: PayloadAction<boolean>) {
      state.ipdbLayersLoaded.geoModel = action.payload;
    },
    setIpdbControlWellsLoaded(state, action: PayloadAction<boolean>) {
      state.ipdbLayersLoaded.controlWells = action.payload;
    },
    resetMapSlice: () => initialState
  }
});

export const {
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setAttentionPolygon,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setAttentionWells,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setExploitableArea,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setFlyToGroup,
  /** @deprecated Use `useSetAtom(hoveredWellAtom)` from `atoms/interaction.ts` instead. Removal deadline: 2026-04-30 */
  setHoveredWell,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setIsDebuggingMap,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setLayers,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setMapExtent,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setMapGroupBy,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setMapState,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setSelectedWells,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setSelectedFacilities,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setTypeLogWells,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setTxnId,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setViewLock,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setXdaInterceptData,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setXdaLineRequest,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setDisplaySettings,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setLabelDensity,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  resetMapSlice,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setOverrideSizeLimit,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setHasToConfirmSize,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setFacilityOverrideSizeLimit,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setFacilityHasToConfirmSize,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setIsLoadingWellMap,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setIsLoadingFacilityMap,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setWellsConfirmationChoice,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setFacilityConfirmationChoice,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setFlyToSpecificPoint,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setNetworkDepthLegend,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  clearNetworkDepthLegend,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setShowNetworkOptions,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setHighlightContributingWellsForFacility,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  clearHighlightContributingWellsForFacility,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setWellNetworkMapRequest,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  clearWellNetworkMapRequest,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setWellNetworkFilterActivated,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setNetworkEnvelope,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setSalesEnvelope,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setIpdbGeoModelLoaded,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setIpdbControlWellsLoaded,
  /** @deprecated Use Zustand `useMapStore`. Removal deadline: 2026-04-30 */
  setBaseLayerResourcePlays
} = mapSlice.actions;

/**
 * @deprecated Use Zustand `useMapStore` actions instead. Removal deadline: 2026-04-30
 */
export const setSelectedEntities =
  (
    entityKind: EntityKind,
    selectedEntities: {
      [name: string]: ISelectedWell;
    }
  ): AppThunk =>
  (dispatch) => {
    if (entityKind === EntityKind.Well) {
      return dispatch(setSelectedWells(selectedEntities));
    } else {
      return dispatch(setSelectedFacilities(selectedEntities));
    }
  };

const update = _debounce((dispatch, layers) => {
  dispatch(setLayers(layers));
}, 500);

/**
 * @deprecated Use Zustand `useMapStore` actions instead. Removal deadline: 2026-04-30
 */
export const updateLayers = (layers) => (dispatch) => {
  update(dispatch, layers);
};

export interface IGroupByMap {
  groupByField: string;
  dataType: string;
  canBin: boolean;
  bin: mBinSize;
}

/**
 * @deprecated Use `useClearWellMap` from `stores/map/hooks` instead. Removal deadline: 2026-04-30
 */
export const clearWellMap = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  if (state.map.txnId.id === CLEAR_MAP_ID) return;
  dispatch(setTxnId({ ...state.map.txnId, id: CLEAR_MAP_ID }));
};

/**
 * @deprecated Use `useClearFacilityMap` from `stores/map/hooks` instead. Removal deadline: 2026-04-30
 */
export const clearFacilityMap = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  if (state.map.txnId.facilityId === CLEAR_MAP_ID) return;
  dispatch(setTxnId({ ...state.map.txnId, facilityId: CLEAR_MAP_ID }));
};

// Debounced thunks for hiding confirmation dialogs
const debouncedHideWellConfirmation = _debounce((dispatch) => {
  dispatch(setHasToConfirmSize(false));
}, 800);

const debouncedHideFacilityConfirmation = _debounce((dispatch) => {
  dispatch(setFacilityHasToConfirmSize(false));
}, 800);

/**
 * @deprecated Use `useHideConfirmationDebounced` from `stores/map/hooks` instead. Removal deadline: 2026-04-30
 */
export const hideWellConfirmationDebounced = (): AppThunk => (dispatch) => {
  debouncedHideWellConfirmation(dispatch);
};

/**
 * @deprecated Use `useHideConfirmationDebounced` from `stores/map/hooks` instead. Removal deadline: 2026-04-30
 */
export const hideFacilityConfirmationDebounced = (): AppThunk => (dispatch) => {
  debouncedHideFacilityConfirmation(dispatch);
};

export default mapSlice.reducer;

/**
 * @deprecated Use `selectFacilityOverrideSizeLimit` from `stores/map/mapSelectors` instead. Removal deadline: 2026-04-30
 */
export const selectFacilityOverrideSizeLimit = (state: { map: MapState }) =>
  state.map.facilityOverrideSizeLimit;

/**
 * @deprecated Use `selectFacilityHasToConfirmSize` from `stores/map/mapSelectors` instead. Removal deadline: 2026-04-30
 */
export const selectFacilityHasToConfirmSize = (state: { map: MapState }) =>
  state.map.facilityHasToConfirmSize;

/**
 * @deprecated Use `selectWellsConfirmationChoice` from `stores/map/mapSelectors` instead. Removal deadline: 2026-04-30
 */
export const selectWellsConfirmationChoice = (state: { map: MapState }) =>
  state.map.wellsConfirmationChoice;

/**
 * @deprecated Use `selectFacilityConfirmationChoice` from `stores/map/mapSelectors` instead. Removal deadline: 2026-04-30
 */
export const selectFacilityConfirmationChoice = (state: { map: MapState }) =>
  state.map.facilityConfirmationChoice;

/**
 * @deprecated Use `selectLayerIsVisibleById` from `stores/map/mapSelectors` instead. Removal deadline: 2026-04-30
 */
export const selectLayerIsVisibleById =
  (layerId: string) => (state: { map: MapState }) => {
    const layer = state.map.layers?.find((layer) => layer.id === layerId);
    return layer?.isVisible ?? false;
  };

/**
 * @deprecated Use `selectIpdbGeoModelLoaded` from `stores/map/mapSelectors` instead. Removal deadline: 2026-04-30
 */
export const selectIpdbGeoModelLoaded = (state: { map: MapState }) =>
  state.map.ipdbLayersLoaded.geoModel;

/**
 * @deprecated Use `selectIpdbControlWellsLoaded` from `stores/map/mapSelectors` instead. Removal deadline: 2026-04-30
 */
export const selectIpdbControlWellsLoaded = (state: { map: MapState }) =>
  state.map.ipdbLayersLoaded.controlWells;

/**
 * @deprecated Use `selectIsXdaLineReversed` from `stores/map/mapSelectors` instead. Removal deadline: 2026-04-30
 *
 * Returns true when the XDA line's first click (A) is east of the second click (A'),
 * meaning the viewer's left side corresponds to A' and right side to A.
 * Used to swap A/A' labels in the XDA viewer so they match the map.
 */
export const selectIsXdaLineReversed = (state: { map: MapState }): boolean => {
  const line = state.map.xdaLineRequest?.line;
  if (!line || line.length < 2) return false;
  return line[0][0] > line[1][0];
};
